import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Sparkles, Zap, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { PRICING, VALUE_STACK, TOTAL_VALUE, PlanTier } from "@/config/pricing";
import { ScrollReveal } from "./ScrollReveal";

const plans: { key: Exclude<PlanTier, "free">; badge?: string; highlight?: boolean }[] = [
  { key: "starter" },
  { key: "creator", badge: "Most Popular", highlight: true },
  { key: "pro", badge: "Best Value" },
];

export const PricingSection = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <section id="pricing" className="py-10 md:py-20 relative" style={{ background: "#FAFAFA" }}>
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <ScrollReveal>
          <div className="text-center mb-8 md:mb-14">
            <h2 className="font-display font-bold mb-3 md:mb-4" style={{ color: "#111827", fontSize: "clamp(24px, 3.5vw, 40px)", lineHeight: 1.2 }}>
              Simple Pricing. <span style={{ color: "#F59E0B" }}>Pick Your Plan.</span>
            </h2>
            <p className="text-sm md:text-lg max-w-2xl mx-auto" style={{ color: "#4B5563" }}>
              Every plan includes a 7-day free trial. No charge today. Cancel anytime before your trial ends.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <div className="max-w-3xl mx-auto mb-8 md:mb-10 rounded-2xl p-4 md:p-6 bg-white" style={{ border: "1px solid #E5E7EB" }}>
            <h3 className="text-center font-display font-bold text-lg mb-4" style={{ color: "#111827" }}>What You're Getting</h3>
            <div className="space-y-2 mb-4">
              {VALUE_STACK.map((item) => (
                <div key={item.name} className="flex items-center justify-between text-sm">
                  <span style={{ color: "#4B5563" }}>{item.name}</span>
                  <span className="font-semibold" style={{ color: "#F59E0B" }}>{item.value}</span>
                </div>
              ))}
            </div>
            <div className="pt-3 flex items-center justify-between font-bold" style={{ borderTop: "1px solid #E5E7EB" }}>
              <span style={{ color: "#111827" }}>Total value:</span>
              <span style={{ color: "#F59E0B" }}>{TOTAL_VALUE}</span>
            </div>
            <p className="text-center text-sm mt-2" style={{ color: "#6B7280" }}>
              Your price: <span className="font-semibold" style={{ color: "#F59E0B" }}>from $39/month</span>
            </p>
          </div>
        </ScrollReveal>

        <div className="flex justify-center mb-8 md:mb-12">
          <div className="inline-flex items-center rounded-full p-1 min-w-[280px]" style={{ background: "#F3F4F6" }}>
            <button
              onClick={() => setIsAnnual(false)}
              className={cn("flex-1 rounded-full px-6 py-2.5 text-sm font-semibold transition-all")}
              style={!isAnnual ? { background: "#7C3AED", color: "#fff" } : { color: "#6B7280" }}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={cn("flex-1 rounded-full px-6 py-2.5 text-sm font-semibold transition-all flex flex-col items-center")}
              style={isAnnual ? { background: "#7C3AED", color: "#fff" } : { color: "#6B7280" }}
            >
              <span>Annual</span>
              <span className="text-[10px] font-bold" style={{ color: isAnnual ? "rgba(255,255,255,0.7)" : "#7C3AED" }}>Save up to $198</span>
            </button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto mb-8 md:mb-12 rounded-2xl p-4 md:p-6 bg-white" style={{ border: "1px solid #E5E7EB" }}>
          <p className="text-xs md:text-sm leading-relaxed text-center" style={{ color: "#6B7280" }}>
            <span className="font-semibold" style={{ color: "#111827" }}>💡 What's a credit?</span>{" "}
            1 credit = 1 AI action. Generating a digital product = 5 credits. Writing a script = 2 credits.
            Clipping a video = 3 credits. Building a sales page = 4 credits. Building a lead magnet = 4 credits.
            Credits reset every month.
          </p>
        </div>

        <ScrollReveal>
          <div className="grid md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto items-end">
            {plans.map(({ key, badge, highlight }) => {
              const plan = PRICING[key];
              const price = isAnnual ? Math.round((plan as any).annualPrice / 12) : plan.price;
              const savings = isAnnual ? (plan as any).annualSavings : 0;

              return (
                <div
                  key={key}
                  className={cn(
                    "rounded-2xl p-5 md:p-7 relative overflow-hidden flex flex-col bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg",
                    highlight ? "md:scale-105 order-first md:order-none" : ""
                  )}
                  style={{
                    border: highlight ? "2px solid #7C3AED" : "1px solid #E5E7EB",
                    boxShadow: highlight ? "0 0 30px rgba(124,58,237,0.08)" : "none",
                  }}
                >
                  {badge && (
                    <span
                      className="absolute top-4 right-4 px-4 py-1 text-xs font-semibold rounded-full flex items-center gap-1"
                      style={highlight
                        ? { background: "#7C3AED", color: "#FFFFFF" }
                        : { background: "#F3F4F6", color: "#6B7280" }
                      }
                    >
                      {highlight ? <Sparkles className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                      {badge}
                    </span>
                  )}

                  <div className="relative z-10 flex flex-col flex-1">
                    <h3 className="font-display text-xl font-bold mb-1" style={{ color: "#111827" }}>{plan.name}</h3>
                    <p className="text-xs md:text-sm mb-4 md:mb-5" style={{ color: "#6B7280" }}>{plan.description}</p>

                    <div className="mb-4 md:mb-5">
                      <div className="flex items-baseline gap-1">
                        <span className="font-display text-4xl md:text-5xl font-bold" style={{ color: "#111827" }}>${price}</span>
                        <span style={{ color: "#9CA3AF" }}>/mo</span>
                      </div>
                      {isAnnual && savings > 0 && (
                        <p className="text-xs mt-1 font-semibold" style={{ color: "#7C3AED" }}>Save ${savings}/year</p>
                      )}
                      <p className="text-sm mt-1 font-medium" style={{ color: "#7C3AED" }}>
                        {plan.credits.toLocaleString()} credits/month
                      </p>
                      <p className="text-xs mt-1 flex items-center gap-1" style={{ color: "#9CA3AF" }}>
                        <Clock className="w-3 h-3" /> 7-day free trial included
                      </p>
                    </div>

                    <div className="mb-3">
                      <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#9CA3AF" }}>Tools included</p>
                      <ul className="space-y-1.5">
                        {plan.tools.map((tool, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#7C3AED" }} />
                            <span className="text-xs" style={{ color: "#4B5563" }}>{tool}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {"bonuses" in plan && (plan as any).bonuses && (
                      <div className="mb-5 flex-1">
                        <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#9CA3AF" }}>Bonuses</p>
                        <ul className="space-y-1.5">
                          {((plan as any).bonuses as string[]).map((bonus, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#7C3AED" }} />
                              <span className="text-xs" style={{ color: "#4B5563" }}>{bonus}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="mt-auto">
                      <Link to="/checkout">
                        <Button variant="hero" size="lg" className="w-full group max-w-[400px] btn-interactive">
                          Start Your Free Trial
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollReveal>

        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 mt-6 md:mt-8 text-xs md:text-sm" style={{ color: "#9CA3AF" }}>
          <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" style={{ color: "#7C3AED" }} /> Cancel anytime</span>
          <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" style={{ color: "#7C3AED" }} /> Credits roll over monthly</span>
          <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" style={{ color: "#7C3AED" }} /> 7-day free trial on all plans</span>
        </div>
      </div>
    </section>
  );
};
